import { MATCH_DEFAULT } from "@/constants";
import chess_scenarios from "@/data/chess-scenarios.json";
import { MatchesAPI } from "@/services/api";
import { firestore } from "@/services/firebase";
import { getTrimmedMean } from "@/utils";
import { serverTimestamp } from "@/utils/firebase-helper";
import MoveHelper from "@/utils/move-helper";
import firebase from "firebase/app";
import { GAME_OVERRIDE, LEEWAY_DURATION } from "vue-chess-shared/constants";
import { delayMs } from "vue-chess-shared/utils";
import { aiPlayer, isAiPlayer } from "vue-chess-shared/utils/ai-helper";
import { isNumeric } from "vue-chess-shared/utils/number-helper";
import { mapGetters } from "vuex";

const matchesRef = firestore.collection("matches");

export default {
  name: "GameRenderless",

  data() {
    return {
      matchId: null,
      matchData: {},
      match: {},
      whitePlayer: {},
      blackPlayer: {},
      fetchedMoves: null,
      moveIndex: null,
      moveTime: 0,
      currentPgn: "",
      firestoreTimeOffset: null,
      whiteTime: 0,
      blackTime: 0,
      whiteTimeLeft: null,
      blackTimeLeft: null,
      whiteBufferUsed: 0,
      blackBufferUsed: 0,
      whiteBufferLeft: 0,
      blackBufferLeft: 0,
      whiteTimeOffset: 0,
      blackTimeOffset: 0,
      timerInterval: null,
      bindFirestore: true,
      gameResult: null,
    };
  },

  computed: {
    ...mapGetters(["getUserId", "getProfile"]),

    moves() {
      if (!this.fetchedMoves) return null;
      return this.fetchedMoves.sort(
        (a, b) => a.move_number - b.move_number || a.createdAt - b.createdAt
      );
    },

    userId() {
      return this.getUserId;
    },

    isLoading() {
      return !this.match || !this.match.createdAt || !this.moves;
    },

    playerColor() {
      if (!this.blackPlayer || !this.whitePlayer) return "";
      if (this.blackPlayer && this.blackPlayer.user_id === this.userId) {
        return "black";
      } else if (this.whitePlayer && this.whitePlayer.user_id === this.userId) {
        return "white";
      }
      return "";
    },

    opponentColor() {
      if (this.playerColor === "black") return "white";
      if (this.playerColor === "white") return "black";
      return null;
    },

    opponent() {
      if (this.playerColor === "black") return this.whitePlayer;
      if (this.playerColor === "white") return this.blackPlayer;
      return null;
    },

    lastMove() {
      if (!this.moves || !this.moves.length) return null;
      return this.moves[this.moves.length - 1];
    },

    currentFen() {
      return this.lastMove ? this.lastMove.fen : "";
    },

    history() {
      return this.moves.map((m) => m.move);
    },

    matchTime() {
      return this.match && this.match.duration
        ? this.match.duration * 1
        : 15 * 60;
    },

    bufferTime() {
      return this.match ? this.match.buffer_duration || 0 : 0;
    },

    matchStart() {
      return this.moves.length ? this.moves[0].createdAt : null;
    },

    blackToStart() {
      if (this.isLoading) return null;
      return this.match.initial_fen && this.match.initial_fen.includes(" b ")
        ? 1
        : 0;
    },

    turnColor() {
      if (this.isLoading) return null;
      const colorChanged = this.match.result
        ? this.moveIndex >= 0
          ? (this.moveIndex + 1) % 2
          : 0
        : this.moves.length % 2;
      return (this.blackToStart + colorChanged) % 2 ? "black" : "white";
    },

    abortDuration() {
      if (
        this.match &&
        !this.match.tournamentId &&
        this.match.gameOverride !== GAME_OVERRIDE.TOURNAMENT
      ) {
        return LEEWAY_DURATION;
      }
      return 0;
    },

    pgnPrefix() {
      if (this.matchData && this.matchData.scenario_id) {
        const scenario = chess_scenarios.find(
          (s) => s.Level === this.matchData.scenario_id
        );
        if (!scenario || !scenario.Setting) return "";
        return `[SetUp "1"]\n[FEN "${scenario.Setting}"]\n`;
      }
      return "";
    },

    matchRef() {
      return matchesRef.doc(this.matchId);
    },

    movesRef() {
      return this.matchRef.collection("moves");
    },

    movesStats() {
      return MoveHelper.movesStats(this.match, this.moves, this.blackToStart);
    },

    blackIncrement() {
      return this.movesStats.reduce(
        (rv, m, i) =>
          i <= this.moveIndex && (i + this.blackToStart) % 2 && m.increment
            ? rv + (m.increment || 0)
            : rv,
        0
      );
    },

    whiteIncrement() {
      return this.movesStats.reduce(
        (rv, m, i) =>
          i <= this.moveIndex && (i + this.blackToStart + 1) % 2 && m.increment
            ? rv + (m.increment || 0)
            : rv,
        0
      );
    },

    blackMatchTime() {
      if (!this.match || !this.match.blackDuration) return null;
      return this.match.blackDuration;
    },

    whiteMatchTime() {
      if (!this.match || !this.match.whiteDuration) return null;
      return this.match.whiteDuration;
    },

    blackTotalTime() {
      return (
        (this.blackMatchTime || this.matchTime) + (this.blackIncrement || 0)
      );
    },

    whiteTotalTime() {
      return (
        (this.whiteMatchTime || this.matchTime) + (this.whiteIncrement || 0)
      );
    },

    isAiGame() {
      return (
        (this.match && this.match.gameOverride == GAME_OVERRIDE.PVAI) ||
        isAiPlayer(this.whitePlayer) ||
        isAiPlayer(this.blackPlayer)
      );
    },

    aiLevel() {
      if (!this.isAiGame) return 0;
      return (
        this.whitePlayer.aiLevel ||
        this.blackPlayer.aiLevel ||
        MATCH_DEFAULT.AI_LEVEL
      );
    },
  },

  watch: {
    matchId: {
      immediate: true, // call it upon creation too
      handler(matchId) {
        if (!matchId) return;
        this.$bind("match", this.matchRef);
        this.$bind("fetchedMoves", this.movesRef);
        this.getMatchFromSql(this.matchId);
      },
    },

    match: {
      handler(match) {
        if (!match || !match.match_id) return;
        if (match.result) {
          this.stopTimer();
          setTimeout(() => {
            if (this.matchData && !this.matchData.player1_rating) {
              console.log("1s later, try to reload sql result");
              this.getMatchFromSql(this.matchId);
            }
          }, 1500);
        } else if (this.match.status === "live") {
          this.startTimer();
        }

        this.whitePlayer = match.player1;
        this.blackPlayer = match.player2;
        if (this.whitePlayer && this.whitePlayer.stats) {
          this.whitePlayer.stats.rating_diff = null;
        }
        if (this.blackPlayer && this.blackPlayer.stats) {
          this.blackPlayer.stats.rating_diff = null;
        }
      },
    },

    matchTime: {
      immediate: true,
      handler() {
        if (!this.matchTime) return;
        this.whiteTimeLeft = this.whiteTotalTime - this.whiteTime;
        this.blackTimeLeft = this.blackTotalTime - this.blackTime;
      },
    },

    moves: {
      immediate: true,
      handler(moves) {
        if (!moves || !moves.length) return;
        const lastMove = moves[moves.length - 1];
        if (!lastMove.createdAt) return;
        if (this.moveIndex !== moves.length - 1) {
          this.loadMove(moves.length - 1);

          this.whiteTimeOffset = getTrimmedMean(
            moves
              .filter((m) => m.color === "white")
              .map((m) => m.createdAt - m.localTimestamp),
            0.25
          );
          this.blackTimeOffset = getTrimmedMean(
            moves
              .filter((m) => m.color === "black")
              .map((m) => m.createdAt - m.localTimestamp),
            0.25
          );

          if (!this.match.result && lastMove.color === this.playerColor) {
            // If I made the last move, update my firestoreTimeOffset
            const newTimeOffset =
              this.playerColor === "white"
                ? this.whiteTimeOffset
                : this.playerColor === "black"
                ? this.blackTimeOffset
                : this.firestoreTimeOffset;
            console.log("update timeOffset", newTimeOffset);
            if (isNumeric(newTimeOffset)) {
              this.firestoreTimeOffset = newTimeOffset;
              sessionStorage.setItem("firestoreTimeOffset", newTimeOffset);
            }
          }
        }

        // Below logics are for 2 players only
        if (this.match.result) return;
        if (!this.playerColor) return;
        if (lastMove.color === this.opponentColor) {
          // If opponent made the last move, update receivedAt attribute
          if (!lastMove.received) {
            this.movesRef.doc(lastMove.id).update({
              received: true,
              receivedAt: serverTimestamp(),
            });
          }
        }
      },
    },
  },

  methods: {
    commenceMatch(date) {
      if (this.match.commencedAt) return;
      if (!this.playerColor) return;
      console.log("commenceMatch", date);

      this.matchRef.update({
        commencedAt: date ? date : serverTimestamp(),
      });
      console.log("Match commenced");
    },

    isMyMove(moveNumber) {
      if (!this.playerColor) return false;
      return (
        (this.playerColor === "white" &&
          (this.blackToStart + moveNumber) % 2) ||
        (this.playerColor === "black" &&
          (this.blackToStart + moveNumber + 1) % 2)
      );
    },

    isAiMove(moveNumber) {
      return this.isAiGame && !this.isMyMove(moveNumber);
    },

    handleMove(data) {
      if (!data.history.length) return;
      const lastMove = data.history[data.history.length - 1];
      if (!lastMove) return;

      const moveNumber = data.history.length;
      if (!this.playerColor) return;
      const isMyMove = this.isMyMove(moveNumber);
      const isAiMove = this.isAiMove(moveNumber);
      if (!isMyMove && !isAiMove) return;

      if (
        (this.abortDuration && moveNumber === 2) ||
        (!this.abortDuration && moveNumber === 1)
      ) {
        this.commenceMatch();
      }

      this.movesRef
        .doc(`${("000" + moveNumber).slice(-3)}`)
        .set({
          player_id: isAiMove
            ? this.opponent.username
            : this.getProfile.username,
          color: isAiMove ? this.opponentColor : this.playerColor,
          received: this.isAiGame ? true : false,
          receivedAt: this.isAiGame ? serverTimestamp() : null,
          move_number: moveNumber,
          move: lastMove,
          orig: data.orig,
          dest: data.dest,
          fen: data.fen,
          pgn: data.pgn,
          createdAt:
            data.isPremove && this.lastMove && this.lastMove.receivedAt
              ? this.lastMove.receivedAt
              : serverTimestamp(),
          localTimestamp: new Date(),
          isPremove: !!data.isPremove,
        })
        .catch(function(error) {
          console.error("Error creating move: ", error);
        });

      // save last move to SQLDB
      const duration = this.moveTime;
      MatchesAPI.postMatchMove(this.matchId, {
        move_number: moveNumber,
        value: [data.fen, `${data.orig}-${data.dest}`, lastMove].join("~"),
        duration: duration ? Math.floor(duration * 1000) : 0, // convert to ms
        ai_player_id: isAiMove ? this.opponent.username : null,
      });
    },

    loadMove(moveIndex) {
      if (moveIndex < 0) {
        moveIndex = -1;
        this.moveIndex = -1;
        this.currentPgn = this.match.initial_fen
          ? `[SetUp "1"]\n[FEN "${this.match.initial_fen}"]\n`
          : this.pgnPrefix + "\n";
      } else {
        moveIndex = Math.min(moveIndex, this.moves.length - 1);
        this.moveIndex = moveIndex;
        this.currentPgn = this.pgnPrefix + this.moves[moveIndex].pgn;
      }

      // keep timers live if match is still live when checking previous moves
      if (!this.match.result && this.moveIndex < this.moves.length - 1) return;

      let whiteTime = 0,
        blackTime = 0,
        whiteBufferUsed = 0,
        blackBufferUsed = 0;

      if (moveIndex >= 0) {
        const movesStats = MoveHelper.movesStats(
          this.match,
          this.moves,
          this.blackToStart
        );
        const playerMove = movesStats[moveIndex];
        const opponentMove = moveIndex > 0 ? movesStats[moveIndex - 1] : null;
        const whiteMove =
          playerMove.color === "white" ? playerMove : opponentMove;
        const blackMove =
          playerMove.color === "black" ? playerMove : opponentMove;

        whiteTime = whiteMove ? whiteMove.timeUsed : 0;
        blackTime = blackMove ? blackMove.timeUsed : 0;
        whiteBufferUsed = whiteMove ? whiteMove.bufferUsed : 0;
        blackBufferUsed = blackMove ? blackMove.bufferUsed : 0;
      }

      this.whiteTime = whiteTime;
      this.blackTime = blackTime;
      this.whiteTimeLeft = this.whiteTotalTime - this.whiteTime;
      this.blackTimeLeft = this.blackTotalTime - this.blackTime;
      this.whiteBufferUsed = whiteBufferUsed;
      this.blackBufferUsed = blackBufferUsed;
      this.whiteBufferLeft = this.bufferTime - this.whiteBufferUsed;
      this.blackBufferLeft = this.bufferTime - this.blackBufferUsed;

      if (this.match.result && this.moveIndex === this.moves.length - 1) {
        const lastMoveAt = this.moves.length
          ? this.lastMove.createdAt.toMillis()
          : (this.match.commencedAt || this.match.startedAt).toMillis();

        // Obcused case of this client ended the match and the endedAt has not comeback
        const matchEndedAt = this.match.endedAt
          ? this.match.endedAt.toMillis()
          : Date.now() + 1000 * this.firestoreTimeOffset;
        this.moveTime = (matchEndedAt - lastMoveAt) / 1000;
        if (this.moves.length >= 2 || !this.abortDuration) {
          if (this.turnColor === "black") this.blackTimeLeft -= this.moveTime;
          else this.whiteTimeLeft -= this.moveTime;
        }
      }
    },

    getPieces(color) {
      if (!this.lastMove) return [];
      if (!this.lastMove.fen) return [];
      const rowsFen = this.lastMove.fen.split(" ")[0];
      if (!rowsFen) return [];
      if (color === "white") {
        return rowsFen.match(/[KQRBNP]/g) || [];
      } else if (color === "black") {
        return rowsFen.match(/[kqrbnp]/g) || [];
      }
      return [];
    },

    isBareKing(color) {
      return this.getPieces(color).length === 1;
    },

    async canEndGame() {
      this.stopTimer();
      if (!this.userId) return false; // anonymous user can't end a match
      if (isNaN(this.firestoreTimeOffset)) return false; // required firestoreTimeOffset
      if (!this.playerColor) {
        return false;
      } else if (this.playerColor !== this.turnColor) {
        await delayMs(2000); // give 2s for the opponent to resolve a match
      }
      return this.match && !this.match.result;
    },

    async handleTimeout(color) {
      if (!(await this.canEndGame())) return;
      console.log("handleTimeout", color);

      if (
        (color === "white" && this.isBareKing("black")) ||
        (color === "black" && this.isBareKing("white"))
      ) {
        this.saveMatchResult(0.5, 0.5, "bare king");
      } else if (this.whiteTimeLeft < 0 && color === "white") {
        this.saveMatchResult(0, 1, "timeout");
      } else if (this.blackTimeLeft < 0 && color === "black") {
        this.saveMatchResult(1, 0, "timeout");
      }
    },

    async handleAbort() {
      if (!(await this.canEndGame())) return;
      console.log("handleAbort");

      this.saveMatchResult(null, null, "abort");
    },

    async handleForfeit(color) {
      if (!(await this.canEndGame())) return;
      console.log("handleForfeit", color);

      let whiteScore = 0,
        blackScore = 0;
      if (color === "white") blackScore = 1;
      if (color === "black") whiteScore = 1;
      this.saveMatchResult(whiteScore, blackScore, "forfeit");
    },

    async handleGameOver(result) {
      console.log("Game Over:", result);
      this.gameResult = result;
      if (!(await this.canEndGame())) return;

      let whiteScore = 0,
        blackScore = 0;
      switch (result) {
        case "checkmate":
          if (this.turnColor === "black") whiteScore = 1;
          else if (this.turnColor === "white") blackScore = 1;
          break;
        case "stalemate":
        case "threefold_repetition":
        case "insufficient_material":
        case "draw":
        case "game over":
          whiteScore = 0.5;
          blackScore = 0.5;
          break;
        default:
          console.error("invalid game_over result", result);
          break;
      }
      this.saveMatchResult(whiteScore, blackScore, result);
    },

    handleResign() {
      if (!this.playerColor) return;
      this.stopTimer();
      if (this.match.result) return;
      this.saveMatchResult(
        this.playerColor === "black" ? 1 : 0,
        this.playerColor === "white" ? 1 : 0,
        "resign"
      );
    },

    handlePlayerAbort() {
      if (!this.playerColor) return;
      this.stopTimer();
      if (this.match.result) return;
      this.saveMatchResult(null, null, `${this.playerColor}_abort`);
    },

    handleRequestDraw() {
      console.log("handleRequestDraw");
      if (this.match.result) return;
      if (this.requestDrawAt) return;
      this.movesRef.doc(this.lastMove.id).update({
        requestDrawColor: this.playerColor,
        requestDrawAt: serverTimestamp(),
      });
    },

    handleDenyDraw() {
      console.log("handleDenyDraw");
      if (this.match.result) return;
      if (!this.lastMove.requestDrawAt) return;
      if (this.lastMove.responseDraw) return;
      this.movesRef.doc(this.lastMove.id).update({
        responseDraw: "deny",
        responseDrawAt: serverTimestamp(),
      });
    },

    handleAcceptDraw() {
      this.stopTimer();
      if (this.match.result) return;
      if (!this.lastMove.requestDrawAt) return;
      if (this.lastMove.responseDraw) return;
      this.movesRef.doc(this.lastMove.id).update({
        responseDraw: "accept",
        responseDrawAt: serverTimestamp(),
      });
      this.saveMatchResult(0.5, 0.5, "draw by agreement");
    },

    calculateMoveTime() {
      // No move yet
      if (!this.moves.length) {
        if (!this.match.startedAt) return -1; // match hasn't started yet
        return (
          firebase.firestore.Timestamp.fromDate(new Date()) -
          (this.match.commencedAt || this.match.startedAt) +
          this.firestoreTimeOffset
        );
      }

      // Match finished
      if (this.match.endedAt) {
        return this.match.endedAt - this.lastMove.createdAt;
      }

      // new move created but firebase hasn't return createdAt yet
      if (!this.lastMove.createdAt) return 0;

      return (
        firebase.firestore.Timestamp.fromDate(new Date()) -
        (this.lastMove.receivedAt || this.lastMove.createdAt) +
        this.firestoreTimeOffset
      );
    },

    startTimer() {
      if (this.timerInterval) return;
      this.timerInterval = setInterval(() => {
        if (this.isLoading) return;
        if (this.match.result) return this.stopTimer();

        this.moveTime = this.calculateMoveTime();

        //Game hasn't started
        if (this.moveTime < 0) {
          return;
        }

        if (
          this.abortDuration &&
          this.match.commencedAt &&
          !this.moves.length
        ) {
          console.error(
            "Match has commenced but 0 moves loaded",
            this.match.commencedAt
          );
          return;
        }

        // if no move was made during abort time or match time
        if (this.playerColor && this.moves.length < 2) {
          if (this.abortDuration > 0 && this.moveTime > this.abortDuration) {
            console.log({
              moveTime: this.moveTime,
              lastMove: this.lastMove,
              moves: this.moves,
              match: this.match,
              firestoreTimeOffset: this.firestoreTimeOffset,
            });
            this.handleAbort();
            return;
          }

          if (!this.match.commencedAt && this.moveTime > LEEWAY_DURATION) {
            this.commenceMatch(
              new Date(this.match.startedAt.toMillis() + LEEWAY_DURATION * 1000)
            );
            return;
          }

          if (this.moveTime > this.matchTime) {
            this.handleForfeit(this.turnColor);
            return;
          }
        }

        // if there's an abortDuration, only deduct player time after the 2nd move
        this.updateTimeLeft();
      }, 300);
    },

    updateTimeLeft() {
      if (!this.match.commencedAt) return;

      const { timeLeft, bufferLeft } = MoveHelper.getPlayerTimers({
        timeTotal:
          this.turnColor === "black"
            ? this.blackTotalTime
            : this.whiteTotalTime,
        timeUsed: this.turnColor === "black" ? this.blackTime : this.whiteTime,
        bufferTotal: this.bufferTime,
        bufferUsed:
          this.turnColor === "black"
            ? this.blackBufferUsed
            : this.whiteBufferUsed,
        moveTime: this.moveTime,
        lastMove: this.lastMove,
      });

      if (this.turnColor === "black") {
        this.blackTimeLeft = timeLeft;
        this.blackBufferLeft = bufferLeft;
        this.whiteTimeLeft = this.whiteTotalTime - this.whiteTime;
      } else if (this.turnColor === "white") {
        this.whiteTimeLeft = timeLeft;
        this.whiteBufferLeft = bufferLeft;
        this.blackTimeLeft = this.blackTotalTime - this.blackTime;
      }

      if (timeLeft < 0) this.handleTimeout(this.turnColor);
    },

    stopTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    },

    async getMatchFromSql(gameId) {
      await MatchesAPI.getMatch(gameId).then(async (data) => {
        if (!data) return;

        this.matchData = data;
        await delayMs(2000);
        if (this.matchData.ended) this.updatePlayersStats(); //update stats (rating after match and diff)

        // firebase binded successfully
        if (this.match && this.match.createdAt) return;

        if (
          !this.isAiGame && // not a vs AI game
          !(this.matchData.player_1_score + this.matchData.player_2_score)
        ) {
          return;
        }

        // No Firebase data, map SQL data to use
        this.bindFirestore = false;
        if (this.match && this.match.match_id) {
          await this.$unbind("match");
        }

        if (this.fetchedMoves && this.fetchedMoves.length) {
          await this.$unbind("fetchedMoves");
        }

        this.match = {
          match_id: this.matchData.match_id,
          player1: this.matchData.player1,
          player2: this.matchData.player2,
          gameOverride: this.matchData.gameOverride,
          duration: this.matchData.duration
            ? this.matchData.duration
            : MATCH_DEFAULT.DURATION,
          variant: this.matchData.variant
            ? this.matchData.variant
            : MATCH_DEFAULT.VARIANT,
          player_1_score: this.matchData.player_1_score / 2,
          player_2_score: this.matchData.player_2_score / 2,
          status:
            this.matchData.player_1_score + this.matchData.player_2_score
              ? "finished"
              : "aborted",
          result: this.matchData.result ? this.matchData.result : "end",
          createdAt: firebase.firestore.Timestamp.fromDate(
            new Date(this.matchData.created)
          ),
          startedAt: firebase.firestore.Timestamp.fromDate(
            new Date(this.matchData.started)
          ),
          endedAt: this.matchData.ended
            ? firebase.firestore.Timestamp.fromDate(
                new Date(this.matchData.ended)
              )
            : null,
        };
        if (!this.match.player1) {
          this.match.player1 = aiPlayer(this.matchData.player_1_id);
        }
        if (!this.match.player2) {
          this.match.player2 = aiPlayer(this.matchData.player_2_id);
        }

        // Get moves from SQLDB
        await MatchesAPI.getMatchMoves(gameId).then((moves) => {
          if (!moves) return;
          let pgn = "";
          // map SQLDB move to Firestore move
          const convertedMoves = moves
            .sort((a, b) => a.move_id - b.move_id)
            .map((move, i) => {
              const s = move.value.split("~");
              pgn += i % 2 ? `${s[2]} ` : `${i / 2 + 1}. ${s[2]} `;
              return {
                id: move.move_id,
                move_number: move.move_number ? move.move_number : i + 1,
                fen: s[0],
                move: s[2],
                pgn: pgn.trim(),
                createdAt: firebase.firestore.Timestamp.fromDate(
                  new Date(move.created)
                ),
              };
            });
          this.fetchedMoves = convertedMoves;
        });
      });
    },

    async saveMatchResult(whiteScore, blackScore, result) {
      console.log("saveMatchResult:", whiteScore, blackScore, result);
      this.matchRef.update({
        player_1_score: whiteScore,
        player_2_score: blackScore,
        result: result,
        status: result === "abort" ? "aborted" : "finished",
        endedAt: serverTimestamp(),
        endedBy: this.userId,
        pgn: this.lastMove && this.lastMove.pgn ? this.lastMove.pgn : "",
      });

      // Save to SQL DB
      const updatedMatch = await MatchesAPI.putMatch(
        this.matchId,
        this.match.gameOverride,
        whiteScore,
        blackScore,
        result,
        this.moves.length
      );
      console.log("updatedMatch", updatedMatch);
      if (updatedMatch) {
        this.matchData = { ...this.matchData, ...updatedMatch };
        this.updatePlayersStats();
      }
    },

    //update stats (rating after match and diff)
    updatePlayersStats() {
      if (this.whitePlayer && this.matchData.player1_rating) {
        this.whitePlayer = {
          ...this.whitePlayer,
          stats: {
            ...this.whitePlayer.stats,
            ...this.matchData.player1_rating,
          },
        };
      }
      if (this.blackPlayer && this.matchData.player2_rating) {
        this.blackPlayer = {
          ...this.blackPlayer,
          stats: {
            ...this.blackPlayer.stats,
            ...this.matchData.player2_rating,
          },
        };
      }
      // hide rating_diff before the match
      if (this.match && !this.match.result) {
        if (this.whitePlayer && this.whitePlayer.stats) {
          this.whitePlayer.stats.rating_diff = null;
        }
        if (this.blackPlayer && this.blackPlayer.stats) {
          this.blackPlayer.stats.rating_diff = null;
        }
      }
    },
  },

  created() {
    if (!this.firestoreTimeOffset) {
      this.firestoreTimeOffset = parseFloat(
        sessionStorage.getItem("firestoreTimeOffset")
      );
    }
    if (!isNumeric(this.firestoreTimeOffset)) this.firestoreTimeOffset = 0;
  },

  beforeDestroy() {
    this.stopTimer();
  },
};
